import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import classnames from 'classnames'
import { LabeledField } from '../../components/Controls'
import { useEscClose } from '../../components/useEscClose'
import { useUploadBaseCaseModelMutation } from './baseCaseUploadApi'
import type { BaseCaseUploadResponse } from './baseCaseUploadApi'

// Import slide-over (v001 §10) — right slide-over following the
// VarianceExplorer drawer pattern. Closes via Escape (useEscClose), the
// backdrop, and the Close/Cancel buttons.

export type ConflictMode = 'replace' | 'upsert' | 'new'

const CONFLICT_MODES: Array<{ value: ConflictMode; label: string; hint: string }> = [
  { value: 'replace', label: 'Delete old & replace', hint: 'Remove the existing model before importing (origin link required).' },
  { value: 'upsert', label: 'Upsert (default)', hint: 'Update overlapping months, insert new ones.' },
  { value: 'new', label: 'New rows only', hint: 'Skip months that already exist.' }
]

const INPUT_CLASS =
  'rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 ' +
  'text-sm text-slate-900 dark:text-slate-100 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500'

export interface ImportDrawerProps {
  propertyName?: string | null
  onClose: () => void
}

export const ImportDrawer: React.FC<ImportDrawerProps> = ({ propertyName, onClose }) => {
  const [file, setFile] = useState<File | null>(null)
  const [mode, setMode] = useState<ConflictMode>('upsert')
  const [originLink, setOriginLink] = useState('')
  const [notes, setNotes] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [result, setResult] = useState<BaseCaseUploadResponse | null>(null)
  const [failed, setFailed] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [upload, { isLoading }] = useUploadBaseCaseModelMutation()

  useEscClose(true, onClose)

  // Origin link is required for replace mode (§10) — gate submit until provided.
  const originMissing = mode === 'replace' && originLink.trim() === ''
  const canSubmit = file != null && !originMissing && !isLoading

  const onSubmit = (): void => {
    if (file == null || !canSubmit) return
    setFailed(false)
    setResult(null)
    // The backend DTO (BaseCaseModelUploadDTO) accepts exactly: file (multipart),
    // shouldPersist, nameResolutions. Conflict mode / origin link / notes have no
    // backend fields yet, so they are captured in the form but NOT posted —
    // posting unknown fields risks tripping request validation.
    upload({ file, shouldPersist: true })
      .unwrap()
      .then(setResult)
      .catch(() => setFailed(true))
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Import base case model">
      <button type="button" aria-label="Close" className="flex-1 bg-black/40" onClick={onClose} />
      <div className="h-screen w-full max-w-xl shrink-0 overflow-y-auto bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Import Base Case Model</h3>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {propertyName != null && propertyName !== '' ? propertyName : 'Upload a pro-forma export'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            Close
          </button>
        </div>

        <div className="space-y-4 p-4">
          {/* File picker: drag & drop plus browse. */}
          <div
            onDragOver={(e) => {
              e.preventDefault()
              setDragOver(true)
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragOver(false)
              const dropped = e.dataTransfer.files?.[0]
              if (dropped != null) setFile(dropped)
            }}
            className={classnames(
              'flex flex-col items-center gap-1 rounded-lg border-2 border-dashed px-4 py-6 text-center',
              dragOver
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10'
                : 'border-slate-300 dark:border-slate-600'
            )}
          >
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Drag &amp; drop a <span className="font-mono text-xs">.csv</span> /{' '}
              <span className="font-mono text-xs">.xlsx</span> file here, or{' '}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
              >
                browse
              </button>
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              aria-label="Base case file"
              className="hidden"
              onChange={(e) => {
                const picked = e.target.files?.[0]
                if (picked != null) setFile(picked)
              }}
            />
            {file != null ? (
              <p className="font-mono text-xs text-slate-700 dark:text-slate-200">{file.name}</p>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500">No file selected</p>
            )}
            <a
              href="/base_case_model_template.csv"
              download="base_case_model_template.csv"
              className="text-xs text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Download blank template
            </a>
          </div>

          {/* Conflict mode. */}
          <fieldset>
            <legend className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              On conflict
            </legend>
            <div className="mt-2 space-y-2">
              {CONFLICT_MODES.map((opt) => (
                <label key={opt.value} className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    type="radio"
                    name="basecase-conflict-mode"
                    value={opt.value}
                    checked={mode === opt.value}
                    onChange={() => setMode(opt.value)}
                    className="mt-0.5 h-4 w-4 border-slate-300 text-indigo-600 focus:ring-indigo-500 dark:border-slate-600"
                  />
                  <span>
                    {opt.label}
                    <span className="block text-xs text-slate-400 dark:text-slate-500">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>

          <LabeledField label={mode === 'replace' ? 'Origin link (required for replace)' : 'Origin link'} htmlFor="basecase-origin">
            <input
              id="basecase-origin"
              type="url"
              value={originLink}
              onChange={(e) => setOriginLink(e.target.value)}
              placeholder="https://docs.google.com/…"
              className={INPUT_CLASS}
            />
          </LabeledField>

          <LabeledField label="Notes" htmlFor="basecase-notes">
            <textarea
              id="basecase-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Why is this model being uploaded?"
              className={INPUT_CLASS}
            />
          </LabeledField>

          <p className="text-xs text-slate-400 dark:text-slate-500">
            Conflict mode, origin link and notes are not yet persisted by the upload API — the file is imported with
            the importer&apos;s default duplicate handling.
          </p>

          {/* Inline result. */}
          {failed && (
            <p className="text-sm text-red-600 dark:text-red-400">Upload failed — check the file and try again.</p>
          )}
          {result != null &&
            (result.success ? (
              <div className="text-sm text-green-600 dark:text-green-400">
                <p>
                  Processed {result.recordsProcessed} rows — {result.recordsCreated} created, {result.recordsSkipped}{' '}
                  skipped.
                </p>
              </div>
            ) : (
              <div className="text-sm text-red-600 dark:text-red-400">
                <p>Upload rejected.</p>
                {(result.errors ?? []).map((err) => (
                  <p key={err} className="text-xs">
                    {err}
                  </p>
                ))}
              </div>
            ))}

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-700">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSubmit}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? 'Uploading…' : 'Upload'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default ImportDrawer
